import makeApiRequest from '@src/apiCalls/makeApiRequest';
import { API_ENDPOINTS } from '@src/constants/apiEndpoints';
import apiRequestConfig from '@src/interfaces/apiEndpoints.interface';
import { useNotification } from '@src/providers/NotificationProviders';
import { Button, Drawer, Flex, Form, Select, Switch, Tooltip } from 'antd';
import React, { use, useEffect, useRef, useState } from 'react';

interface CostcoPreferenceDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}
const CostcoPreferenceDrawer: React.FC<CostcoPreferenceDrawerProps> = ({
  open,
  setOpen,
}) => {
  const openNotification = useNotification();
  const hasShown403Notification = useRef(false);
  const [validateMember, setValidateMember] = useState(false);
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialValidateMember, setInitialValidateMember] = useState(false);
  const [initialSelectedTiers, setInitialSelectedTiers] = useState<string[]>(
    []
  );
  const [tierOptions, setTierOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const token = localStorage.getItem('costcoAdminToken');

  const fetchData = async () => {
    return makeApiRequest(
      {},
      API_ENDPOINTS.GET_SYSTEM_PREFERENCES as apiRequestConfig,
      '',
      {
        Authorization: `Bearer ${token}`,
      }
    )
      .then((data) => {
        const useApi = data?.data?.useCostcoApi?.value === 'true';
        const rawTiers = data?.data?.costcoValidTiers?.value;
        const tierArray = rawTiers
          ? rawTiers.split(',').map((t: string) => t.trim())
          : [];
        const acceptedTierValues =
          data?.data?.costcoValidTiers?.accepted_values;
        const options = acceptedTierValues
          ? acceptedTierValues.split(',').map((t: string) => ({
              label: t.trim(),
              value: t.trim(),
            }))
          : [];
        setTierOptions(options);

        // set current values
        setValidateMember(useApi);
        setSelectedTiers(tierArray);

        // set initial values
        setInitialValidateMember(useApi);
        setInitialSelectedTiers(tierArray);
      })
      .catch((error) => {
        if (error?.status === 403 && !hasShown403Notification.current) {
          hasShown403Notification.current = true;
          openNotification('Error in loading system preferences', 'Error');
        } else if (error?.status !== 403) {
          openNotification(error?.message, 'Error');
        }
      });
  };

  const hasChanges =
    validateMember !== initialValidateMember ||
    selectedTiers.join(',') !== initialSelectedTiers.join(',');

  const postData = async () => {
    setLoading(true);
    return makeApiRequest(
      {
        useCostcoApi: validateMember,
        costcoValidTiers: selectedTiers.join(', '),
      },
      API_ENDPOINTS.POST_SYSTEM_PREFERENCES as apiRequestConfig,
      '',
      {
        Authorization: `Bearer ${token}`,
      }
    )
      .then((res) => {
        setLoading(false);
        if (res.status === 200) {
          openNotification(
            'System preferences updated successfully',
            'Success'
          );
        }
        setOpen(false);
        setValidateMember(false);
        setSelectedTiers([]);
      })
      .catch((error) => {
        setLoading(false);
        if (error?.status === 403 && !hasShown403Notification.current) {
          hasShown403Notification.current = true;
          openNotification('Error in updating system preferences', 'Error');
        } else if (error?.status !== 403) {
          openNotification(error?.message, 'Error');
        }
      });
  };

  const handleClose = () => {
    setOpen(false);
    setValidateMember(false);
    setSelectedTiers([]);
  };

  const handleSave = () => {
    postData();
  };

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  return (
    <Drawer
      title="System Preferences"
      open={open}
      onClose={handleClose}
      footer={
        <Flex justify="flex-end" gap={5}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={loading}
            disabled={!hasChanges}
          >
            Save
          </Button>
        </Flex>
      }
      width={600}
    >
      <Form layout="vertical">
        <Form.Item>
          <Flex align="center" gap={5}>
            <label>Validate MemberId With CostcoAPI:</label>
            <Switch checked={validateMember} onChange={setValidateMember} />
          </Flex>
        </Form.Item>

        <Form.Item label="Eligible Memberships Tiers">
          <Tooltip
            title={
              validateMember
                ? ''
                : 'Turn ON validate MemberId with CostcoAPI switch to enable this'
            }
          >
            <Select
              mode="multiple"
              placeholder="Select tiers"
              disabled={!validateMember}
              value={selectedTiers}
              onChange={setSelectedTiers}
              options={tierOptions}
            />
          </Tooltip>
        </Form.Item>
      </Form>
    </Drawer>
  );
};
export default CostcoPreferenceDrawer;
